import { cloneDeep } from 'lodash';

import { defineStore } from 'pinia';
import { createGetTheSessionIdFromTheAccessTokenQuery } from 'src/stores/authentication/queries';
import { USER } from 'src/stores/user/localStorageKeys';
import { SETUP_WIZARD_COMPLETED_KEY } from 'src/stores/userInterfaceSettings/localStorageKeys';

import {
	InstanceAuthenticationConfiguration,
	SessionIdForToken,
	createSessionIdForTokenRunType,
	normalizeInstanceAuthenticationConfiguration,
	normalizeInstanceAuthenticationConfigurations,
} from 'src/models/authentication';

import {
	ACTIVE_INSTANCE_AUTHENTICATION_NAME_KEY,
	INSTANCE_AUTHENTICATION_CONFIGURATION_KEY,
} from 'src/services/authentication';
import { userService } from 'src/services/authentication/user.service';

import { createGetTheSessionIdFromTheAccessTokenCallback } from './callbacks';
import {
	AddInstanceAuthenticationConfigurationPayload,
	AuthenticationStoreState,
	GetTheSessionIdFromTheTokenPayload,
	LoginWithUsernameAndPasswordPayload,
	LogoutPayload,
	WebauthnDeviceRegistrationPayload,
	WebauthnLoginPayload,
} from './interfaces';

const readAllInstanceAuthenticationConfigurations = (): Array<InstanceAuthenticationConfiguration> => {
	const serializedConfigurations = localStorage.getItem(INSTANCE_AUTHENTICATION_CONFIGURATION_KEY);

	if (serializedConfigurations === null || serializedConfigurations === '') {
		return [];
	}

	try {
		const parsedConfigurations = JSON.parse(serializedConfigurations) as Array<InstanceAuthenticationConfiguration>;
		return normalizeInstanceAuthenticationConfigurations(parsedConfigurations);
	} catch {
		return [];
	}
};

const clearStoredActiveAuthentication = (): void => {
	localStorage.removeItem(ACTIVE_INSTANCE_AUTHENTICATION_NAME_KEY);
	localStorage.removeItem(USER);
};

const readActiveInstanceAuthenticationConfiguration = (
	allConfigurations: Array<InstanceAuthenticationConfiguration>
): InstanceAuthenticationConfiguration | null => {
	const activeConfigurationName = localStorage.getItem(ACTIVE_INSTANCE_AUTHENTICATION_NAME_KEY) ?? '';
	if (activeConfigurationName === '') {
		return null;
	}

	const activeConfiguration =
		allConfigurations.find(
			(instanceConfiguration) =>
				instanceConfiguration !== null &&
				typeof instanceConfiguration.name === 'string' &&
				instanceConfiguration.name === activeConfigurationName
		) ?? null;

	if (activeConfiguration === null) {
		clearStoredActiveAuthentication();
	}

	return activeConfiguration;
};

const createDefaultState = (): AuthenticationStoreState => {
	const allInstanceAuthenticationConfigurations = readAllInstanceAuthenticationConfigurations();
	const activeInstanceAuthenticationConfiguration = readActiveInstanceAuthenticationConfiguration(
		allInstanceAuthenticationConfigurations
	);
	const activeInstanceAuthenticationConfigurationName = activeInstanceAuthenticationConfiguration?.name ?? '';

	return {
		activeInstanceAuthenticationConfiguration,
		activeInstanceAuthenticationConfigurationName,
		allInstanceAuthenticationConfigurations,
		loggedIn: activeInstanceAuthenticationConfiguration
			? activeInstanceAuthenticationConfiguration.user !== ''
			: false,
		loginWithKeyFailed: false,
		loginWithUsernameAndPasswordFailed: false,
		loginWithUsernameAndPasswordPending: false,
		pongHost: '',
		pongPassword: '',
		pongPort: '',
		pongUsername: '',
		pongVhost: '',
		sessionId: '',
		webauthnDeviceRegistrationFailed: null,
	};
};

export const useAuthenticationStore = defineStore('authentication', {
	actions: {
		addInstanceAuthenticationConfiguration(payload: AddInstanceAuthenticationConfigurationPayload): void {
			const normalizedPayload = normalizeInstanceAuthenticationConfiguration(payload);
			const allInstanceAuthenticationConfigurationsData = cloneDeep<Array<InstanceAuthenticationConfiguration>>(
				this.allInstanceAuthenticationConfigurations
			);

			allInstanceAuthenticationConfigurationsData.push(normalizedPayload);

			localStorage.setItem(
				INSTANCE_AUTHENTICATION_CONFIGURATION_KEY,
				JSON.stringify(allInstanceAuthenticationConfigurationsData)
			);

			this.setInstanceAuthenticationConfigurations(allInstanceAuthenticationConfigurationsData);
		},
		adoptStoredActiveInstanceAuthentication(): void {
			const allInstanceAuthenticationConfigurations = readAllInstanceAuthenticationConfigurations();
			const activeInstanceAuthenticationConfiguration = readActiveInstanceAuthenticationConfiguration(
				allInstanceAuthenticationConfigurations
			);

			this.allInstanceAuthenticationConfigurations = allInstanceAuthenticationConfigurations;
			this.activeInstanceAuthenticationConfiguration = activeInstanceAuthenticationConfiguration;
			this.activeInstanceAuthenticationConfigurationName = activeInstanceAuthenticationConfiguration?.name ?? '';
		},
		async getTheSessionIdFromTheToken(payload: GetTheSessionIdFromTheTokenPayload): Promise<void> {
			if (payload.queryStore !== undefined) {
				await payload.queryStore.query<SessionIdForToken>({
					callback: createGetTheSessionIdFromTheAccessTokenCallback((sessionId) =>
						this.setSessionId(sessionId)
					),
					createTime: new Date(),
					query: createGetTheSessionIdFromTheAccessTokenQuery(
						payload.instanceAuthenticationConfiguration.token
					),
					responseType: 'json',
					runType: createSessionIdForTokenRunType(),
				});
				return;
			}

			this.setSessionId('');
		},
		loginWithKeyFailure(): void {
			this.loggedIn = false;
			this.loginWithKeyFailed = true;
		},
		loginWithKeySuccess(): void {
			this.loggedIn = true;
			this.loginWithKeyFailed = false;
		},
		loginWithUsernameAndPassword(payload: LoginWithUsernameAndPasswordPayload): void {
			this.loginWithUsernameAndPasswordPending = true;
			void userService
				.login(
					payload.t,
					payload.instance,
					payload.username,
					payload.password,
					payload.twoFactorAuthenticationCode,
					payload.eventBus
				)
				.then(() => {
					this.adoptStoredActiveInstanceAuthentication();
					this.loginWithUsernameAndPasswordSuccess();
				})
				.catch(() => {
					this.loginWithUsernameAndPasswordFailure();
				});
		},
		loginWithUsernameAndPasswordFailure(): void {
			this.loggedIn = false;
			this.loginWithUsernameAndPasswordPending = false;
			this.loginWithUsernameAndPasswordFailed = true;
		},
		loginWithUsernameAndPasswordSuccess(): void {
			this.loggedIn = true;
			this.loginWithUsernameAndPasswordPending = false;
			this.loginWithUsernameAndPasswordFailed = false;
		},
		async logout(payload: LogoutPayload): Promise<void> {
			const activeInstanceAuthenticationConfiguration = this.activeInstanceAuthenticationConfiguration;

			await userService.logout(
				this.activeInstanceAuthenticationConfigurationName,
				activeInstanceAuthenticationConfiguration?.user ?? '',
				payload.eventBus
			);

			this.logoutState();
		},
		logoutState(): void {
			this.loggedIn = false;
			this.loginWithUsernameAndPasswordPending = false;
			this.loginWithUsernameAndPasswordFailed = false;
			this.loginWithKeyFailed = false;
			this.activeInstanceAuthenticationConfigurationName = '';

			localStorage.removeItem(USER);
			localStorage.removeItem(SETUP_WIZARD_COMPLETED_KEY);
		},
		setActiveInstanceAuthenticationConfiguration(payload: InstanceAuthenticationConfiguration | null): void {
			this.activeInstanceAuthenticationConfiguration =
				payload === null ? null : normalizeInstanceAuthenticationConfiguration(payload);
		},
		setActiveInstanceAuthenticationName(payload: string): void {
			this.activeInstanceAuthenticationConfigurationName = payload;
			localStorage.setItem(ACTIVE_INSTANCE_AUTHENTICATION_NAME_KEY, payload);

			this.setActiveInstanceAuthenticationConfiguration(
				this.allInstanceAuthenticationConfigurations.find(
					(instanceAuthenticationConfiguration) => instanceAuthenticationConfiguration.name === payload
				) ?? null
			);
		},
		setInstanceAuthenticationConfigurations(payload: Array<InstanceAuthenticationConfiguration>): void {
			const normalizedConfigurations = normalizeInstanceAuthenticationConfigurations(payload);
			this.allInstanceAuthenticationConfigurations = normalizedConfigurations;
			localStorage.setItem(INSTANCE_AUTHENTICATION_CONFIGURATION_KEY, JSON.stringify(normalizedConfigurations));
		},
		setSessionId(payload: string): void {
			this.sessionId = payload;
		},
		webauthnDeviceRegistration(payload: WebauthnDeviceRegistrationPayload): void {
			void userService
				.registerWebauthnDevice(
					payload.t,
					payload.instance,
					payload.username,
					payload.password,
					payload.twoFactorAuthenticationCode
				)
				.then((registrationSuccessful) => {
					if (registrationSuccessful) {
						this.webauthnDeviceRegistrationSuccess();
						return;
					}

					this.webauthnDeviceRegistrationFailure();
				})
				.catch(() => {
					this.webauthnDeviceRegistrationFailure();
				});
		},
		webauthnDeviceRegistrationFailure(): void {
			this.webauthnDeviceRegistrationFailed = true;
		},
		webauthnDeviceRegistrationStateClear(): void {
			this.webauthnDeviceRegistrationFailed = null;
		},
		webauthnDeviceRegistrationSuccess(): void {
			this.webauthnDeviceRegistrationFailed = false;
		},
		webauthnLogin(payload: WebauthnLoginPayload): void {
			void userService
				.webauthnLogin(payload.t, payload.instance)
				.then(() => {
					this.adoptStoredActiveInstanceAuthentication();
					this.loginWithKeySuccess();
				})
				.catch(() => {
					this.loginWithKeyFailure();
				});
		},
	},
	getters: {
		instanceAuthenticationConfigurationCount(state): number {
			return state.allInstanceAuthenticationConfigurations.length;
		},
		user(state): null | string {
			return state.activeInstanceAuthenticationConfiguration
				? state.activeInstanceAuthenticationConfiguration.user
				: null;
		},
	},
	state: createDefaultState,
});
export * from './interfaces';
export * from './types';
